import { USERNAME_ISSUE_MESSAGE, USERNAME_MAX_LENGTH, usernameIssue } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { useState } from "react";
import { useTerminology } from "../lib/terminology.js";
import { usernameErrorMessage, useSaveUsername } from "../lib/username.js";
import { ErrorBanner } from "./ErrorBanner.js";

/**
 * The shopper's username form: one field, the rules checked as they type (the same
 * `usernameIssue` the server applies), and the server's word on whether the name is taken.
 */
export function UsernameDialog({
  open,
  onOpenChange,
  username,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current username, null while none is chosen. */
  username: string | null;
}) {
  const { battle } = useTerminology();
  const save = useSaveUsername();
  const [draft, setDraft] = useState(username ?? "");
  const [error, setError] = useState<string | null>(null);
  const candidate = draft.trim();
  // Nothing is said about an empty field; the rules speak once the shopper has typed.
  const issue = candidate ? usernameIssue(candidate) : null;
  const unchanged = candidate === username;

  const reset = (next: boolean) => {
    if (next) {
      setDraft(username ?? "");
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate || issue || unchanged) return;
    setError(null);
    save.mutate(candidate, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => setError(usernameErrorMessage(err)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{username ? "Change username" : "Choose a username"}</DialogTitle>
          <DialogDescription>
            Other players see it in {battle.plural}. Letters, numbers and underscores, 3 to 20
            characters.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={submit}>
          <div className="grid gap-1.5">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={draft}
              maxLength={USERNAME_MAX_LENGTH}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          {issue && <p className="text-xs text-destructive">{USERNAME_ISSUE_MESSAGE[issue]}</p>}
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={!candidate || issue !== null || unchanged || save.isPending}
            >
              {save.isPending ? "Saving…" : "Save username"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
